<template>
  <div class="row">
    <div class="col-sm-2">
      <OverviewBox title="User Overview" :loading="loading" :avatarImage="logoURL" :avatarName="userData.name" :avatarCircleMode="avatarCircleMode" :avatarEditable="userHasUpdatePermission(PermissionTypes.UserPermissionTypes.user)" @editImage="editAvatarImage" @removeImage="removeAvatarImage">
        <h4>{{userData.name}}</h4>
        <p>
          <span class="label label-account-owner" v-if="userData.isOwner">Account Owner</span>
        </p>
        <h5>User Groups</h5>
        <p>
          <a class="label label-empty label-overview-box mr-1" v-for="group in userData.userGroups" :key="`${group.id}-link`" href="#" v-show="userHasReadPermission(PermissionTypes.UserPermissionTypes.userGroup)" @click.prevent="navigateUserGroup(group.id)">
            <i class="fas fa-circle pr-1" :style="{'color': group.colourTag}"> </i> {{group.name}}
          </a>
          <span class="label label-empty label-overview-box mr-1" v-for="group in userData.userGroups" :key="group.id" v-show="!userHasReadPermission(PermissionTypes.UserPermissionTypes.userGroup)">
            <i class="fas fa-circle pr-1" :style="{'color': group.colourTag}"> </i> {{group.name}}
          </span>
        </p>
        <template v-if="userData.labelList && userData.labelList.length > 0">
          <h5>Labels</h5>
          <p>
            <span class="label label-empty label-overview-box mr-1" v-for="label in userData.labelList" :key="label.id"><i class="fas fa-circle pr-1" :style="{color: label.colour}"> </i> {{label.name}}</span>
          </p>
        </template>
        <OverviewBoxInfoItem icon="fa-user">{{userData.title}} {{userData.name}}</OverviewBoxInfoItem>
        <OverviewBoxInfoItem v-if="userData.phone" icon="fa-phone">{{userData.phone}}</OverviewBoxInfoItem>
        <OverviewBoxInfoItem v-if="userData.mobile" icon="fa-mobile-alt">{{userData.mobile}}</OverviewBoxInfoItem>
        <OverviewBoxInfoItem v-if="userData.isMFAEnabled" icon="fa-shield-check">Multi-Factor Enabled</OverviewBoxInfoItem>

        <OverviewBoxList>
          <OverviewBoxListItem title="Status">
            <span v-if="userData.isActive" class="status-active">Active</span>
            <span v-if="!userData.isActive" class="status-archive">Archived</span>
          </OverviewBoxListItem>
          <OverviewBoxListItem title="Created On">{{userData.createdAt | formatDate}}</OverviewBoxListItem>
          <OverviewBoxListItem title="Last Updated">{{userData.updatedAt | formatDate}}</OverviewBoxListItem>
          <OverviewBoxListItem title="Last Login">{{userData.lastLogin | formatDateTime}}</OverviewBoxListItem>
        </OverviewBoxList>
      </OverviewBox>
    </div>
    <div class="col-sm-10">
      <TabbedBox :loading="loading">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-1">
            <i class="far fa-user"></i> User
          </Tab>
          <Tab tabid="tab-2" v-if="userHasReadPermission(PermissionTypes.UserPermissionTypes.permission)">
            <i class="far fa-lock-alt"></i> Permissions
          </Tab>
        </template>
        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-1">
            <DataPageArea title="Details">
              <template slot="actionArea">
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.UserPermissionTypes.user)" class="float-right">
                  <DropdownMenuItem @click="editUser" icon="fa-pencil">Edit User</DropdownMenuItem>
                  <DropdownMenuItem @click="updateUserPassword" icon="fa-key">Update Password</DropdownMenuItem>
                  <DropdownMenuItem v-if="canDisableMFA" @click="disableMFA" icon="fa-mobile">Disable MFA</DropdownMenuItem>
                  <DropdownMenuDivider v-if="!userData.isOwner" />
                  <DropdownMenuItem @click="archiveUser" v-if="userData.isActive && !userData.isOwner" icon="fa-archive">Archive User</DropdownMenuItem>
                  <DropdownMenuItem @click="restoreUser" v-if="!userData.isActive && !userData.isOwner" icon="fa-undo">Restore User</DropdownMenuItem>
                </ActionDropMenu>
              </template>
              <template slot="dataArea">
                <DataItem title="Name">{{userData.title}} {{userData.name}}</DataItem>
                <DataItem title="Email">{{userData.email}}</DataItem>
                <DataItem title="Status">
                  <span v-if="userData.isActive" class="status-active">Active</span>
                  <span v-if="!userData.isActive" class="status-archive">Archived</span>
                </DataItem>
                <DataItem title="Phone">{{userData.phone}}</DataItem>
                <DataItem title="Mobile">{{userData.mobile}}</DataItem>
                <DataItem title="Timezone">{{userData.timezoneCode}}</DataItem>
                <DataItem title="DOB">{{userData.dateOfBirth | formatDate}}</DataItem>
                <DataItem title="Email - Reply To">{{userData.emailReplyToAddress}}</DataItem>
              </template>
            </DataPageArea>
          </TabPage>
          <TabPage tabid="tab-2">
            <div class="row">
              <div class="col-lg-12">
                <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.UserPermissionTypes.permission) && !userData.isOwner" class="float-right">
                  <DropdownMenuItem @click="editPermissions" v-if="!editingPermissions" icon="fa-pencil">Edit Permissions</DropdownMenuItem>
                  <DropdownMenuItem @click="resetPermissions" icon="fa-trash-restore">Reset Permissions</DropdownMenuItem>
                </ActionDropMenu>
                <h3>Permissions</h3>
              </div>
            </div>
            <div class="row" v-if="userData.isOwner">
              <div class="col-lg-4">
                <p>
                <span class="text-muted">User is Account Owner, all permissions are enabled automatically</span>
                </p>
              </div>
            </div>
            <FormGroup :form="permissionForm">
              <PermissionMatrix
                :permissionMap="userData.permissions"
                @update="userPermissions = $event"
                editMode="user"
                :editing="editingPermissions"
              />
              <div v-if="editingPermissions">
                <FormSubmit label="Update Permissions" :submitting="submittingPermissionUpdate" @cancelled="cancelPermissionEditing" @submitted="submitPermissionUpdate" />
              </div>
            </FormGroup>
          </TabPage>
        </template>
      </TabbedBox>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import form from '@/mixins/form'
import PermissionMatrix from '@/modules/users/components/permissionMatrix.vue'

import UserEditDialog from '@/modules/users/views/userEditDialog.vue'
import UserUpdatePasswordDialog from '@/modules/users/views/userUpdatePasswordDialog.vue'
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'
import colourMixin from '@/mixins/colour'

export default {
  name: "User",
  mixins: [form, colourMixin],
  components: {
    PermissionMatrix
  },
  data: () => {
    return {
      breadcrumbs: [
        { title: "Users", link: "/users" },
        { title: "Users" }
      ],
      loading: false,
      userData: {},

      // Permissions
      editingPermissions: false,
      submittingPermissionUpdate: false,
      permissionForm: {},
      userPermissions: {}
    };
  },
  mounted() {
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.requestData()
  },
  computed: {
    ...mapState({
      profileUserIsOwner: state => state.profile.profileUser.isOwner
    }),
    logoURL() {
      if(this.$data.userData.logoURL == null || this.$data.userData.logoURL == '') {
        if(this.$data.userData.title == 'Ms' || this.$data.userData.title == 'Miss' || this.$data.userData.title == 'Mrs')
          return '/glass/img/user-overview-female.png'
        else
          return '/glass/img/user-overview-male.png'
      }
      return this.$data.userData.logoURL
    },
    avatarCircleMode() {
      return this.$data.userData.logoURL != null && this.$data.userData.logoURL != ''
    },
    canDisableMFA() {
      return !this.userData.isOwner && this.profileUserIsOwner && this.userData.isMFAEnabled
    }
  },
  methods: {
    requestData() {
      this.loading = true

      this.$apiService.userService.getUser(this.$route.params.id)
      .then(response => {
        this.$data.userData = response.data;
        this.$data.userPermissions = response.data.permissions

        this.$store.commit("page/setPageTitle", this.$data.userData.name);
      })
      .finally(() => {
        this.loading = false
      })

      // TODO: Refresh the profile if this user is the current logged in user?
    },
    editPermissions() {
      this.$data.editingPermissions = true
    },
    resetPermissions() {
      this.confirmDeleteMessage('Reset User Permissions?', 'Are you sure you want to reset this user\'s permissions?\nAll permissions will be set to inherit from the group permissions')
      .then(success => {
        if(success) {
          this.$apiService.userService.resetUserPermissions(this.$route.params.id)
          .then(response => {
            this.$data.editingPermissions = false
            this.successToast('Permissions Reset')
            this.requestData()
          })
        }
      })
    },
    cancelPermissionEditing() {
      this.editingPermissions = false
      this.permissionForm = {}

      // Refresh data to go back to correct data
      this.requestData()
    },
    submitPermissionUpdate() {
      this.submittingPermissionUpdate = true
      this.permissionForm = {}

      this.$apiService.userService.updateUserPermissions(this.$route.params.id, { permissions: this.$data.userPermissions })
      .then(response => {
        this.submittingPermissionUpdate = false
        this.$data.editingPermissions = false
        this.successToast('Permissions Updated')
      })
      .catch(err => {
        this.submittingPermissionUpdate = false
        this.$data.permissionForm = this.processFormResponse(err)
      })
    },
    editUser() {
      this.displayModal(UserEditDialog, { 
        userId: this.$data.userData.id
      },
      this.$data.userData)
      .then((success) => {
        if(success) {
          this.successToast('User Updated')
          this.requestData()
        }
      })
    },
    updateUserPassword() {
      this.displayModal(UserUpdatePasswordDialog, { 
        userId: this.$data.userData.id
      })
      .then((success) => {
        if(success) {
          this.successToast('Password Updated')
          this.requestData()
        }
      })
    },
    archiveUser() {
      this.confirmDeleteMessage('Archive User?', 'Are you sure you want to archive this user? Once archived, they will not be able to access the system')
      .then(success => {
        if(success) {
          this.$apiService.userService.archiveUser(this.$route.params.id).then(response => {
            this.successToast('User Archived')
            this.requestData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error Archiving User')
          })
        }
      })
    },
    restoreUser() {
      this.confirmDeleteMessage('Restore User?', 'Are you sure you want to restore this user?')
      .then(success => {
        if(success) {
          this.$apiService.userService.restoreUser(this.$route.params.id).then(response => {
            this.successToast('User Restored')
            this.requestData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error Restoring User')
          })
        }
      })
    },
    navigateUserGroup(userGroupId) {
      this.$router.push(`/users/groups/${userGroupId}`)
    },
    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.userData.id,
        APIresource: 'users',
        currentURL: this.$data.userData.logoURL,
        circleCrop: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestData()
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this user\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.userData.id, 'users').then(response => {
            this.successToast('Image Removed')
            this.requestData()
          })
        }
      })
    },
    disableMFA() {
      this.confirmDeleteMessage('Disable Multi Factor Authentication?', 'This will turn off MFA for the user. Are you sure you want to continue?')
      .then(success => {
        if(success) {
          this.$apiService.userService.disableOtherUserMFA(this.userData.id)
          .then(response => {
            this.successToast('MFA Disabled')
            this.requestData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error Disabling MFA')
          })
        }
      })
    }
  }
}

</script>